import { Component, useEffect, useRef, useState } from 'react';
import { useAuth } from '../lib/auth.jsx';
import * as db from '../lib/data.js';
import MaterialEnqueueModal from '../components/MaterialEnqueueModal.jsx';
import { useLeaveGuard } from '../lib/leaveguard.js';
import { toCountryCode } from '../lib/origin.js';
import {
  ACTIVE_STATUSES, CONFIDENCE_LABEL, CONFIDENCE_TONE, SECTIONS, STATUS_LABEL, TIER_LABEL, TIER_TONE, VERDICT_LABEL, VERDICT_TONE,
  approvePlan, bulkBlockers, canBulkApprove, fmtBkk, fmtUsd, groupCurrentValues, groupPrevValues, overwriteKey, overwriteList, retryAt,
  newerApprovedMap, safeHref, supersededMap, thaiOr, triedList, variantsDiffer, verifyInfo, wouldOverwrite,
} from '../lib/materials.js';

// หน้าตรวจผล "หาวัสดุด้วย AI" (/materials) — ผล AI เป็นร่างเสมอ คนกดยืนยันที่นี่ก่อนเขียนลงสินค้า (สัญญา: docs/ai-materials-plan.md)
//   1 การ์ด = 1 รุ่น · ส่วน รอตรวจ / กำลังค้น / ยืนยันแล้ว-ไม่ใช้ / ขัดข้อง
//
// ⚠ กติกาที่ห้ามถอด:
//   · หน้านี้เป็นที่เดียวที่เขียนค่าวัสดุจากผล AI ลงสินค้า — หน้าต่างลงคิวมีแค่ลิงก์มาที่แถวนี้
//   · ยืนยันหลายแถวเฉพาะ canBulkApprove — มีค่าที่ตรวจมือแล้ว 504 แถว ห้ามถูกทับโดยไม่มีใครเห็น · ตรวจซ้ำกับค่าล่าสุดก่อนเขียนจริง
//   · ยืนยันทีละแถว/ใช้กับ SKU เพิ่ม ทุกทางผ่าน passOverwriteGate: อ่านค่าล่าสุด → ต่างจากที่เห็นหรือจะทับค่าเดิม = ถามก่อน ไม่เขียน
//     ปุ่มในแผงถามเขียน "ค่าที่แผงบอก" เสมอ (แก้ข้อความอยู่ = ข้อความนั้น) ไม่มีทางส่งค่า AI ที่ยังไม่แก้ตอนมีค่าที่คนแก้
//   · สีในรุ่นวัสดุไม่เท่ากัน (variants_differ) = ต้องติ๊ก SKU ก่อนยืนยัน
//   · แก้ไทยแล้วไม่แก้อังกฤษ = ไม่ส่งค่าอังกฤษ ช่องอังกฤษถูกล้าง (ห้ามเอาค่า AI ไปประกบค่าที่คนแก้)
//   · ค่าที่พิมพ์ค้าง/ติ๊กค้าง อยู่ใน state ของหน้านี้แยกจากรายการ → รีเฟรชทุก 60 วิไม่ทับ · ลิงก์ทุกอันผ่าน safeHref
//   · ไม่มี <form> — Enter ไม่กดยืนยัน · กดยืนยันซ้ำกันด้วย busyRef

// ── MOCK: ครบทุกสถานะ · ทุกแหล่ง · ทุกผลตรวจ ─────────────────────────────────────
const MOCK_CARDS = {
  'SP-TENT': { id: 'm10', sku: 'SP-TENT', material: '', origin_country: 'JP', hs_code: '63062200' },
  'SP-IGT': { id: 'm11', sku: 'SP-IGT', material: '', origin_country: 'JP' },
  'NG-32-CB': { id: 'm7', sku: 'NG-32-CB', material: 'โคโพลีเอสเตอร์ Tritan Renew', origin_country: 'US' },
  'NG-16-CB': { id: 'm8', sku: 'NG-16-CB', material: '', origin_country: 'US' },
  'TC-095-01': { id: 'm1', sku: 'TC-095-01', material: 'ผ้าโพลีเอสเตอร์', origin_country: 'JP' },
  'TC-095-02': { id: 'mx1', sku: 'TC-095-02', material: 'ผ้าโพลีเอสเตอร์', origin_country: 'JP' },
  'TC-095-03': { id: 'mx2', sku: 'TC-095-03', material: '', origin_country: 'CN' },
  'TC-153': { id: 'm2', sku: 'TC-153', material: 'ผ้าไนลอน', origin_country: 'CN' },
  'TC-282': { id: 'm3', sku: 'TC-282', material: '', origin_country: 'CN' },
  'TC-151': { id: 'm4', sku: 'TC-151', material: '', origin_country: 'CN' },
  'TC-036': { id: 'm5', sku: 'TC-036', material: 'เหล็ก', origin_country: 'CN' },
  'TC-202': { id: 'm6', sku: 'TC-202', material: '', origin_country: 'TW' },
  'NG-KIDS': { id: 'm9', sku: 'NG-KIDS', material: '', origin_country: 'US' },
  'KS-01-BK': { id: 'mx3', sku: 'KS-01-BK', material: '', origin_country: 'VN' },
  'KS-01-WH': { id: 'mx4', sku: 'KS-01-WH', material: 'ผ้าฝ้าย', origin_country: 'VN' },
  'KS-01-RD': { id: 'mx5', sku: 'KS-01-RD', material: '', origin_country: 'VN' },
  'KS-01-GN': { id: 'mx6', sku: 'KS-01-GN', material: '', origin_country: 'VN' },
  'GDN-093-GN': { id: 'mx7', sku: 'GDN-093-GN', material: '', origin_country: 'JP' },
  'GDN-093-RD': { id: 'mx8', sku: 'GDN-093-RD', material: 'ด้าม: ไม้บีช (เช็คจากฉลาก)', origin_country: 'JP' },
  'GDN-090-BK': { id: 'mx9', sku: 'GDN-090-BK', material: '', origin_country: 'JP' },
};
const mockBase = (extra) => ({
  brand: 'Snow Peak', model_code: 'SP', skus: [], late_skus: [], input: {}, result: null, verify: null,
  material_th: null, material_en: null, material_original: null, source_url: null, source_tier: null, source_on_registry: null,
  confidence: null, verify_verdict: null, flags: [], hs_warnings: [], origin_on_page: null, notes_th: null, ai_model: 'claude-opus-5-5',
  cost_usd: null, research_cost_usd: null, attempts: 0, error: null, requested_at: '2026-11-01T02:00:00Z', started_at: null, finished_at: null,
  approved_at: null, approved_edited: null, prev_values: {}, apply_log: [], ...extra,
});
const VERIFIED = (url, evidence) => ({ verdict: 'confirmed', checked_urls: [url], evidence, issues: [], corrected_material_th: '', corrected_material_en: '' });
const MOCK_RESEARCH = [
  mockBase({
    id: 'r-ok-1', status: 'draft', brand: 'Snow Peak', model_code: 'SP-TENT', skus: ['SP-TENT'], confidence: 'high', verify_verdict: 'confirmed',
    material_th: 'ฟลายชีต: โพลีเอสเตอร์ 100%; เสา: อะลูมิเนียมอัลลอย', material_en: 'FLY: POLYESTER 100%; POLE: ALUMINUM ALLOY',
    material_original: 'フライシート：ポリエステル100%\nフレーム：アルミ合金', source_url: 'https://www.snowpeak.co.jp/products/amenity-dome/', source_tier: 'official_primary', source_on_registry: true,
    origin_on_page: 'Japan', cost_usd: 0.28, attempts: 1, notes_th: 'หน้าทางการระบุตารางสเปกชัดเจน',
    result: { product_name_on_page: 'Amenity Dome', variants_differ: false, tried: ['https://www.snowpeak.co.jp/products/amenity-dome/'], sources: [] },
    verify: VERIFIED('https://www.snowpeak.co.jp/products/amenity-dome/', 'ตารางสเปกเขียน フライシート：ポリエステル100% ตรงกับต้นฉบับ'),
    requested_at: '2026-11-01T02:10:00Z',
  }),
  mockBase({
    id: 'r-ok-2', status: 'draft', brand: 'Nalgene', model_code: 'NG-WM', skus: ['NG-32-CB', 'NG-16-CB'], confidence: 'high', verify_verdict: 'confirmed',
    material_th: 'โคโพลีเอสเตอร์ Tritan Renew', material_en: 'TRITAN RENEW COPOLYESTER',
    material_original: 'Tritan Renew', source_url: 'https://nalgene.com/product/wide-mouth/', source_tier: 'official_regional', source_on_registry: true,
    origin_on_page: 'USA', cost_usd: 0.34, attempts: 1,
    result: { product_name_on_page: 'Wide Mouth Bottle', variants_differ: false, tried: [], sources: [] },
    verify: VERIFIED('https://nalgene.com/product/wide-mouth/', 'หน้าเว็บเขียน Made with Tritan Renew'),
    requested_at: '2026-11-01T02:08:00Z',
  }),
  mockBase({
    id: 'r-differs', status: 'draft', brand: 'TOKYOCRAFTS', model_code: 'TC-095', skus: ['TC-095-01', 'TC-095-02', 'TC-095-03'], confidence: 'high', verify_verdict: 'confirmed',
    material_th: 'ผ้านอก: โพลีเอสเตอร์ 75D; พื้น: โพลีเอสเตอร์ 210D', material_en: 'OUTER: POLYESTER 75D; FLOOR: POLYESTER 210D',
    material_original: 'アウター：ポリエステル75D\nフロア：ポリエステル210D', source_url: 'https://tokyocrafts.jp/items/wingfort', source_tier: 'official_primary', source_on_registry: true,
    cost_usd: 0.41, attempts: 1,
    result: { product_name_on_page: 'WINGFORT', variants_differ: false, tried: [], sources: [] },
    verify: VERIFIED('https://tokyocrafts.jp/items/wingfort', 'ตรงกับตารางสเปกบนหน้าเว็บ'),
    requested_at: '2026-11-01T02:06:00Z',
  }),
  mockBase({
    id: 'r-document', status: 'draft', brand: 'TOKYOCRAFTS', model_code: 'TC-153', skus: ['TC-153'], confidence: 'medium', verify_verdict: 'mismatch',
    material_th: 'ตัวกล่อง: ไนลอน 100%', material_en: 'BODY: NYLON 100%',
    material_original: 'Body: Nylon 100%', source_url: 'https://tokyocrafts.jp/catalog/2026.pdf', source_tier: 'official_document', source_on_registry: true,
    flags: ['ผู้ตรวจพบจุดไม่ตรง: แคตตาล็อกปีก่อนเขียนโพลีเอสเตอร์'], cost_usd: 0.73, attempts: 2,
    notes_th: 'แคตตาล็อกปีนี้กับปีก่อนเขียนไม่เหมือนกัน ฝากช่วยเทียบฉลากจริง',
    result: { product_name_on_page: 'Light Soft Container', variants_differ: false, tried: [], sources: [] },
    verify: { verdict: 'mismatch', checked_urls: ['https://tokyocrafts.jp/catalog/2026.pdf', 'https://tokyocrafts.jp/catalog/2025.pdf'], evidence: 'ปี 2026 เขียน Nylon 100% · ปี 2025 เขียน Polyester 100%', issues: ['ปีเอกสารไม่ตรงกัน'], corrected_material_th: '', corrected_material_en: '' },
    requested_at: '2026-11-01T02:05:00Z',
  }),
  mockBase({
    id: 'r-retailer', status: 'draft', brand: 'TOKYOCRAFTS', model_code: 'TC-282', skus: ['TC-282'], confidence: 'medium', verify_verdict: 'unreachable',
    material_th: 'ผ้าตาข่ายโพลีเอสเตอร์', material_en: 'POLYESTER MESH', material_original: 'ポリエステルメッシュ',
    source_url: 'https://www.example-retailer.jp/items/tc-282', source_tier: 'authorized_retailer', source_on_registry: false,
    flags: ['ไม่มีหน้าทางการ ใช้ร้านค้าตัวแทน — ควรเทียบป้ายสินค้าจริงตอนของถึง', 'ยังไม่ผ่านการตรวจซ้ำ (ผู้ตรวจเปิดแหล่งไม่ได้) — ขอให้ช่วยเปิดลิงก์ดูก่อนยืนยัน'],
    hs_warnings: ['พิกัด 4202 ใช้กับกระเป๋า — วัสดุตาข่ายอาจอยู่คนละบรรทัดภาษี ฝากช่วยถามชิปปิ้ง'],
    origin_on_page: 'Vietnam', cost_usd: 0.91, attempts: 1,
    result: { product_name_on_page: 'AIRDRY MULTI POUCH', variants_differ: false, tried: [], sources: [] },
    verify: { verdict: 'unreachable', checked_urls: [], evidence: '', issues: ['เปิดหน้าร้านค้าไม่ได้ (บล็อกตัวดึงข้อมูล)'], corrected_material_th: '', corrected_material_en: '' },
    requested_at: '2026-11-01T02:04:00Z',
  }),
  mockBase({
    id: 'r-market', status: 'draft', brand: 'TOKYOCRAFTS', model_code: 'TC-151', skus: ['TC-151'], confidence: 'low', verify_verdict: 'confirmed',
    material_th: 'ผ้าโพลีเอสเตอร์', material_en: 'POLYESTER', material_original: 'ポリエステル',
    source_url: 'http://shop.example-market.com/tc151', source_tier: 'marketplace', source_on_registry: false,
    flags: ['ไม่มีหน้าทางการ ใช้มาร์เก็ตเพลส (เบาะแสเท่านั้น) — ควรเทียบป้ายสินค้าจริงตอนของถึง'], cost_usd: 1.07, attempts: 1,
    result: { product_name_on_page: 'Tissue Case', variants_differ: false, tried: ['https://tokyocrafts.jp/?s=TC-151 (ไม่พบ)'], sources: [] },
    verify: { verdict: 'confirmed', checked_urls: ['javascript:alert(1)', 'http://shop.example-market.com/tc151', 'https://shop.example-market.com/tc151'], evidence: 'หน้าร้านเขียน ポリエステル', issues: [], corrected_material_th: '', corrected_material_en: '' },
    requested_at: '2026-11-01T02:03:00Z',
  }),
  mockBase({
    id: 'r-variants', status: 'draft', brand: 'Klattermusen', model_code: 'KS-01', skus: ['KS-01-BK', 'KS-01-WH', 'KS-01-RD'], late_skus: ['KS-01-GN'], confidence: 'medium', verify_verdict: 'confirmed',
    material_th: 'ตัวเสื้อ: ฝ้าย 66%, โพลีเอสเตอร์ 34%; ริบ: ฝ้าย 95%, สแปนเด็กซ์ 5%', material_en: 'BODY: COTTON 66%, POLYESTER 34%; RIB: COTTON 95%, SPANDEX 5%',
    material_original: 'Body: 66% cotton, 34% polyester\nRib: 95% cotton, 5% spandex', source_url: 'https://klattermusen.com/products/ks-01', source_tier: 'official_primary', source_on_registry: true,
    flags: ['สีในกลุ่มนี้วัสดุไม่เท่ากัน — ขอให้ช่วยติ๊กเฉพาะ SKU ที่ตรงกับผลนี้ก่อนยืนยัน'], cost_usd: 0.62, attempts: 1,
    result: { product_name_on_page: 'Ymer Tee', variants_differ: true, variant_note: 'สีขาวเป็นฝ้าย 100% ต่างจากสีอื่น — ค่านี้ตรวจจากสีดำ', tried: [], sources: [] },
    verify: VERIFIED('https://klattermusen.com/products/ks-01', 'หน้าเว็บสีดำเขียน 66% cotton, 34% polyester'),
    requested_at: '2026-11-01T02:02:00Z',
  }),
  mockBase({
    id: 'r-offhost', status: 'draft', brand: 'Snow Peak', model_code: 'SP-IGT', skus: ['SP-IGT'], confidence: 'medium', verify_verdict: 'confirmed',
    material_th: 'อะลูมิเนียม; แผ่นท็อป: ไม้ไผ่', material_en: 'ALUMINUM; TOP: BAMBOO', material_original: 'アルミ / 竹',
    source_url: 'https://snowpeak-official-store.example.com/igt', source_tier: 'official_primary', source_on_registry: false,
    flags: ['ลิงก์นี้ไม่อยู่ในรายชื่อเว็บทางการของแบรนด์ — ขอให้ช่วยเปิดดูก่อนยืนยัน'], cost_usd: 0.33, attempts: 1,
    result: { product_name_on_page: 'IGT Frame', variants_differ: false, tried: [], sources: [] },
    verify: VERIFIED('https://snowpeak-official-store.example.com/igt', 'หน้าเว็บเขียนตรงกับต้นฉบับ'),
    requested_at: '2026-11-01T02:01:00Z',
  }),
  mockBase({
    id: 'r-notfound', status: 'not_found', brand: 'TOKYOCRAFTS', model_code: 'TC-202', skus: ['TC-202'], confidence: 'low', cost_usd: 1.07, attempts: 1,
    flags: ['ไม่พบแหล่งที่ระบุวัสดุ'], notes_th: 'เว็บทางการไม่มีหน้าสินค้ารหัสนี้ — ฝากช่วยดูป้ายที่ตัวสินค้า หรือถามซัพพลายเออร์',
    result: { product_name_on_page: '', variants_differ: false, tried: ['https://tokyocrafts.jp/?s=TC-202 (ไม่พบ)', 'ค้นด้วยบาร์โค้ด 4589946136487 (ไม่พบ)', 'javascript:alert(2)'], sources: [] },
    requested_at: '2026-11-01T01:50:00Z',
  }),
  mockBase({
    id: 'r-old', status: 'not_found', brand: 'TOKYOCRAFTS', model_code: 'TC-095', skus: ['TC-095-01'], confidence: 'low', cost_usd: 0.65, attempts: 1,
    flags: ['ไม่พบแหล่งที่ระบุวัสดุ'], notes_th: 'รอบก่อนหาไม่เจอ', result: { tried: ['ค้นรอบแรก'], sources: [] }, requested_at: '2026-10-30T02:00:00Z',
  }),
  mockBase({ id: 'r-q', status: 'queued', brand: 'Nalgene', model_code: 'NG-KIDS', skus: ['NG-KIDS'], requested_at: '2026-11-01T03:00:00Z' }),
  mockBase({ id: 'r-q-back', status: 'queued', brand: 'DoD', model_code: 'K2-610', skus: ['K2-610-KH'], backoff_count: 4, error: 'anthropic 529 overloaded_error', next_attempt_at: new Date(Date.now() + 4 * 60000).toISOString(), requested_at: '2026-11-01T02:56:00Z' }),
  mockBase({ id: 'r-ing', status: 'researching', brand: 'TOKYOCRAFTS', model_code: 'TC-036', skus: ['TC-036'], attempts: 1, cost_usd: 0.12, started_at: '2026-11-01T03:02:00Z', requested_at: '2026-11-01T02:59:00Z' }),
  mockBase({ id: 'r-ed', status: 'researched', brand: 'DoD', model_code: 'C2-534', skus: ['C2-534-KH'], attempts: 1, cost_usd: 0.2, material_th: 'โครง: อะลูมิเนียมอัลลอย', requested_at: '2026-11-01T02:58:00Z' }),
  mockBase({ id: 'r-ver', status: 'verifying', brand: 'DoD', model_code: 'T8-524', skus: ['T8-524-BK', 'T8-524-TN'], attempts: 1, cost_usd: 0.26, material_th: 'ผ้า: โพลีเอสเตอร์', requested_at: '2026-11-01T02:57:00Z' }),
  mockBase({
    id: 'r-appr', status: 'approved', brand: 'BAREBONES', model_code: 'GDN-093', skus: ['GDN-093-BK'], late_skus: ['GDN-093-GN', 'GDN-093-RD'], confidence: 'high', verify_verdict: 'confirmed',
    material_th: 'ด้าม: ไม้โอ๊ก; ใบ: เหล็กกล้าคาร์บอน', material_en: 'HANDLE: OAK; BLADE: CARBON STEEL', material_original: 'Handle: oak / Blade: carbon steel',
    source_url: 'https://barebonesliving.com/products/gdn-093', source_tier: 'official_primary', source_on_registry: true, cost_usd: 0.31, attempts: 1,
    approved_at: '2026-10-31T07:30:00Z', approved_edited: false, finished_at: '2026-10-31T07:30:00Z',
    prev_values: { 'GDN-093-BK': { material: 'เหล็ก', material_en: null, material_source_url: null } },
    apply_log: [{ at: '2026-10-31T07:30:00Z', by: 'mock', skus: ['GDN-093-BK'] }],
    result: { variants_differ: false, tried: [], sources: [] }, verify: VERIFIED('https://barebonesliving.com/products/gdn-093', 'ตรงกับหน้าเว็บ'), requested_at: '2026-10-31T06:00:00Z',
  }),
  mockBase({
    id: 'r-appr-old', status: 'approved', brand: 'BAREBONES', model_code: 'GDN-093', skus: ['GDN-093-BK'], late_skus: ['GDN-093-RD'], confidence: 'medium',
    material_th: 'ด้าม: ไม้; ใบ: เหล็ก', material_en: null, source_url: 'https://barebonesliving.com/old/gdn-093', source_tier: 'official_regional', source_on_registry: true, cost_usd: 0.4, attempts: 1,
    approved_at: '2026-10-20T07:30:00Z', approved_edited: false, finished_at: '2026-10-20T07:30:00Z', result: { variants_differ: false, tried: [], sources: [] }, requested_at: '2026-10-20T06:00:00Z',
  }),
  mockBase({
    id: 'r-appr-ed', status: 'approved', brand: 'CLAYMORE', model_code: 'CLF-500', skus: ['CLF-500CAMO'], confidence: 'low',
    material_th: 'พลาสติก ABS; โลหะอะลูมิเนียม', material_en: null, source_url: null, source_tier: null, source_on_registry: false, cost_usd: 1.1, attempts: 1,
    approved_at: '2026-10-30T04:15:00Z', approved_edited: true, finished_at: '2026-10-30T04:15:00Z', notes_th: 'คนพิมพ์ค่าเองจากฉลากจริง',
    prev_values: { 'CLF-500CAMO': { material: '', material_en: null, material_source_url: null } },
    result: { variants_differ: false, tried: ['https://claymore.example/?s=clf-500 (ไม่พบ)'], sources: [] }, requested_at: '2026-10-30T02:00:00Z',
  }),
  mockBase({ id: 'r-rej', status: 'rejected', brand: 'Nalgene', model_code: 'NG-LID', skus: ['NG-LID-63'], error: 'ค่ากว้างเกินไป จะเช็คจากฉลากเอง', material_th: 'พลาสติก', cost_usd: 0.4, finished_at: '2026-10-29T05:00:00Z', requested_at: '2026-10-29T03:00:00Z' }),
  mockBase({ id: 'r-err', status: 'error', brand: 'SHAKA', model_code: 'SK-217', skus: ['SK-217-BK'], attempts: 3, cost_usd: 0.05, error: 'anthropic 400 invalid_request_error: messages.0.content too long', finished_at: '2026-11-01T01:00:00Z', requested_at: '2026-11-01T00:50:00Z' }),
  mockBase({ id: 'r-err2', status: 'error', brand: 'SHAKA', model_code: 'SK-300', skus: ['SK-300-BK'], attempts: 3, cost_usd: 2.61, error: 'เกินเพดานเงินต่อรุ่น — หยุดเพื่อไม่ให้ค่าใช้จ่ายบานปลาย', finished_at: '2026-11-01T01:10:00Z', requested_at: '2026-11-01T00:55:00Z' }),
];
const MOCK_CONTROL = { paused: false, paused_reason: null, daily_max_usd: 50, per_click_max: 50 };
const MOCK_SPENT = 12.4;

const toCardMap = (list) => Object.fromEntries((list ?? []).map(c => [c.sku, c]));
const has = (v) => String(v ?? '').trim() !== '';
const sameText = (a, b) => String(a ?? '').trim() === String(b ?? '').trim();
const skuUnion = (r) => [...new Set([...(r.skus ?? []), ...(r.late_skus ?? [])])];
// ใบสินค้าที่หน้านี้ต้องรู้ค่าเดิม — แถวรอตรวจทุก SKU · แถวยืนยันแล้วเฉพาะ SKU ที่ยังรอคนเลือก
const skusNeedingCards = (r) => (r.status === 'draft' || r.status === 'not_found' ? skuUnion(r)
  : r.status === 'approved' ? (r.late_skus ?? []) : []);

// ── ส่วนย่อยของการ์ด — ประกาศนอก component หลัก (ใน component = remount ทุก render แล้วช่องที่พิมพ์เสียโฟกัส) ──
const Field = ({ q, wide = false, children }) => (
  <div className={`mr-f ${wide ? 'wide' : ''}`}>
    <div className="mr-q">{q}</div>
    <div className="mr-v">{children}</div>
  </div>
);

const Chip = ({ tone = '', children }) => <span className={`hschip ${tone}`}>{children}</span>;

// ลิงก์จาก AI — https เท่านั้น · ไม่ผ่าน = โชว์เป็นตัวหนังสือพร้อมบอกว่าทำไมไม่ทำเป็นลิงก์
function SafeLink({ url }) {
  const href = safeHref(url);
  if (href) return <a className="mr-link" href={href} target="_blank" rel="noopener noreferrer">{url}</a>;
  return <span className="mr-badurl">{url} <em>(ไม่ใช่ลิงก์ https จึงไม่เปิดให้)</em></span>;
}

function OriginCompare({ row, cards }) {
  const here = [...new Set(skuUnion(row).map(s => String(cards[s]?.origin_country ?? '').trim().toUpperCase()).filter(Boolean))];
  const page = String(row.origin_on_page ?? '').trim();
  if (!page && !here.length) return <span className="mr-dim">ไม่มีข้อมูล</span>;
  const code = page ? toCountryCode(page) : null;
  const differs = !!code && !!here.length && !here.includes(code);
  return (
    <span className={differs ? 'mr-diff' : ''}>
      หน้าเว็บ: {page || 'ไม่ระบุ'} · ในระบบ: {here.join(', ') || 'ยังไม่มี'}
      {differs && <em> — ไม่ตรงกัน ระบบไม่เปลี่ยนให้ ฝากช่วยตรวจ</em>}
    </span>
  );
}

// ช่องติ๊ก SKU พร้อมค่าเดิม — ใช้ทั้งรุ่นที่สีวัสดุต่างกันและ SKU ที่เข้ามาทีหลัง คนต้องเห็นว่าจะทับอะไรก่อนติ๊ก
function SkuTick({ sku, card, next, on, disabled, onChange }) {
  const cur = String(card?.material ?? '').trim();
  const hit = wouldOverwrite(card, next);
  return (
    <label className={`mr-skuchk ${on ? 'on' : ''} ${hit ? 'diff' : ''}`}>
      <input type="checkbox" checked={on} disabled={disabled} onChange={onChange}/>
      <span className="mr-skuname">{sku}</span>
      <span className="mr-skucur">{!card ? 'หาใบสินค้าไม่เจอ' : cur || 'ยังว่าง'}{hit && <b> · จะถูกทับ</b>}</span>
    </label>
  );
}

// การ์ดเดียวพัง (ข้อมูลรูปแปลกจากผล AI) ต้องไม่ทำให้ทั้งหน้าขาว — แถวอื่นยังต้องตรวจต่อได้ · แอพไม่มี ErrorBoundary ชั้นบน
class CardBoundary extends Component {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(e) { console.error('material card', e); this.props.onFail?.(this.props.row.id, true); }
  componentWillUnmount() { if (this.state.failed) this.props.onFail?.(this.props.row.id, false); }   // กลับมาแล้วพังอีกก็ติดป้ายใหม่เอง
  componentDidUpdate(prev) {
    // โหลดแถวใหม่แล้ว (ข้อมูลอาจถูกแก้) = ลองแสดงอีกครั้ง · พังซ้ำก็กลับมาที่ข้อความนี้เอง
    if (prev.row !== this.props.row && this.state.failed) { this.setState({ failed: false }); this.props.onFail?.(this.props.row.id, false); }
  }
  render() {
    if (!this.state.failed) return this.props.children;
    const { row } = this.props;
    return <div className="mr-card"><div className="fhint mr-why" style={{ overflowWrap: 'anywhere' }}>{String(row.model_code ?? '')} · แถวนี้แสดงรายละเอียดไม่ได้ — ฝากช่วยแจ้งแอดมิน แถวอื่นยังใช้งานได้ตามปกติ</div></div>;
  }
}

function ResearchCard({
  row, cards, open, superseded, supersededBy, flash, busy, editing, pickedSkus, rejectText, ask, lateTicks,
  onToggle, onBulkTick, bulkTicked, bulkOk, blockers,
  onApprove, onStartEdit, onEditChange, onCancelEdit, onAgain, onStartReject, onRejectText, onCancelReject, onConfirmReject,
  onPickSku, onPickAll, onCancelOverwrite, onConfirmOverwrite, onLateTick, onApplyLate, onOpenRow,
}) {
  const status = row.status;
  const isReview = status === 'draft' || status === 'not_found';
  const variants = variantsDiffer(row);
  const info = verifyInfo(row);
  const tried = triedList(row);
  // เทียบกับค่าที่จะเขียนจริง: กำลังแก้/พิมพ์อยู่ = ข้อความในช่อง · ไม่ได้แก้ = ผล AI (แถว not_found ยังไม่พิมพ์ = ยังไม่มีอะไรจะทับ)
  const nextValue = editing ? editing.th : row.material_th;
  const groups = isReview ? groupCurrentValues(row.skus, cards, nextValue) : [];
  const picked = pickedSkus ?? [];
  // SKU ที่จะถูกทับค่าเดิม — รุ่นที่สีต่างกันนับเฉพาะตัวที่ติ๊ก (ตัวที่ไม่ติ๊กไม่ถูกเขียน)
  const overwriteHits = (g) => g.skus.filter(x => !variants || picked.includes(x)).length;
  const prev = status === 'approved' ? groupPrevValues(row.prev_values) : [];
  const name = row.result?.product_name_on_page || (Array.isArray(row.input?.product_names) ? row.input.product_names[0] : '');
  const retry = retryAt(row);
  const preview = status === 'not_found' ? 'หาแหล่งที่ระบุวัสดุไม่เจอ — พิมพ์ค่าเองได้'
    : retry ? `จะลองใหม่ ${fmtBkk(retry)}`
    : row.material_th || '—';
  const canAct = isReview && !superseded;
  const thChanged = editing && !sameText(editing.th, row.material_th);
  const enChanged = editing && !sameText(editing.en, row.material_en);

  return (
    <div id={`mr-row-${row.id}`} className={`mr-card st-${status} ${open ? 'open' : ''} ${flash ? 'mr-flash' : ''}`}>
      <div className="mr-head">
        {bulkOk
          ? <label className="mr-tick" title="เลือกแถวนี้เพื่อยืนยันพร้อมกัน">
              <input type="checkbox" checked={bulkTicked} disabled={busy} onChange={() => onBulkTick(row.id)} aria-label={`เลือก ${row.model_code} เพื่อยืนยันพร้อมกัน`}/>
            </label>
          : <span className="mr-tick none"/>}
        <button type="button" className="mr-toggle" aria-expanded={open} onClick={() => onToggle(row.id)}>
          <span className="mr-model">
            <b>{row.model_code}</b>
            <span className="mr-sub">{row.brand} · {(row.skus ?? []).length} SKU{(row.late_skus ?? []).length ? ` · รอเพิ่ม ${row.late_skus.length}` : ''}</span>
          </span>
          <span className="mr-prev" title={preview}>{preview}</span>
          <span className="mr-chips">
            <Chip tone={status === 'approved' ? 'ok' : status === 'error' || status === 'not_found' ? 'dg' : status === 'draft' ? 'miss' : ''}>{STATUS_LABEL[status] ?? 'ไม่ทราบสถานะ'}</Chip>
            {superseded && <Chip>มีผลใหม่กว่าแล้ว</Chip>}
            {retry && <Chip tone="miss">รอลองใหม่</Chip>}
            {row.source_tier && <Chip tone={TIER_TONE[row.source_tier]}>{TIER_LABEL[row.source_tier] ?? 'แหล่งอื่น'}</Chip>}
            {row.confidence && <Chip tone={CONFIDENCE_TONE[row.confidence]}>{CONFIDENCE_LABEL[row.confidence] ?? 'ไม่ระบุความมั่นใจ'}</Chip>}
            {row.verify_verdict && <Chip tone={VERDICT_TONE[row.verify_verdict]}>{VERDICT_LABEL[row.verify_verdict] ?? 'ยังไม่ตรวจซ้ำ'}</Chip>}
          </span>
          <span className="mr-caret" aria-hidden="true">{open ? '▴' : '▾'}</span>
        </button>
      </div>

      {open && (
        <div className="mr-body">
          {name && <div className="mr-name">ชื่อบนหน้าเว็บ: {name}</div>}

          <div className="mr-fields">
            {(editing || has(row.material_th) || isReview) && (
            <Field q="ทำจากอะไร (ไทย)?" wide>
              {editing
                ? <textarea rows={3} value={editing.th} disabled={busy || !!ask} placeholder="เช่น ตัวเสื้อ: ฝ้าย 100%; ซับใน: โพลีเอสเตอร์ 100%"
                    onChange={e => onEditChange(row.id, { th: e.target.value })}/>
                : (row.material_th || <span className="mr-dim">ยังไม่มีค่า</span>)}
            </Field>
            )}
            {(editing || has(row.material_en) || isReview || status === 'approved') && (
            <Field q="ทำจากอะไร (อังกฤษ ตามผู้ผลิต)?" wide>
              {editing
                ? <textarea rows={3} value={editing.en} disabled={busy || !!ask}
                    onChange={e => onEditChange(row.id, { en: e.target.value })}/>
                : (row.material_en || <span className="mr-dim">ไม่มีค่าอังกฤษ</span>)}
              {editing && thChanged && !enChanged && status === 'draft' && (
                <div className="fhint">แก้ภาษาไทยแล้วแต่ยังไม่แก้ภาษาอังกฤษ — ตอนยืนยันระบบจะล้างช่องอังกฤษให้ เพื่อไม่ให้ค่าไม่ตรงกันไปถึงชิปปิ้ง ถ้าอยากเก็บ ฝากช่วยแก้ให้ตรงกับภาษาไทย</div>
              )}
            </Field>
            )}

            {has(row.material_original) && <Field q="หน้าเว็บเขียนว่าอะไร?" wide><pre className="mr-orig">{row.material_original}</pre></Field>}

            {(status === 'draft' || status === 'approved' || has(row.source_url)) && (
              <Field q="ข้อมูลนี้มาจากไหน?" wide>
                {has(row.source_url) ? <SafeLink url={row.source_url}/> : <span className="mr-dim">ไม่มีลิงก์</span>}
                <div className="mr-row">
                  {row.source_tier
                    ? <Chip tone={TIER_TONE[row.source_tier]}>{TIER_LABEL[row.source_tier] ?? 'แหล่งอื่น'}</Chip>
                    : <Chip>ไม่ระบุประเภทแหล่ง</Chip>}
                  {String(row.source_tier ?? '').startsWith('official') && row.source_on_registry === false && <Chip tone="miss">เว็บนี้ไม่อยู่ในทะเบียนของแบรนด์</Chip>}
                </div>
              </Field>
            )}

            {(status === 'draft' || status === 'not_found' || row.confidence) && (
            <Field q="มั่นใจแค่ไหน?">
              {row.confidence ? <Chip tone={CONFIDENCE_TONE[row.confidence]}>{CONFIDENCE_LABEL[row.confidence] ?? 'ไม่ระบุ'}</Chip> : <span className="mr-dim">ยังไม่มีผล</span>}
            </Field>
            )}
            <Field q="ค่าใช้จ่ายเท่าไร?">
              {fmtUsd(row.cost_usd)}{row.attempts > 1 ? ` · ลอง ${row.attempts} ครั้ง` : ''}
            </Field>

            {(status === 'draft' || row.verify_verdict) && (
              <Field q="ตรวจซ้ำแล้วตรงไหม?" wide>
                {row.verify_verdict
                  ? <Chip tone={VERDICT_TONE[row.verify_verdict]}>{VERDICT_LABEL[row.verify_verdict] ?? 'ไม่ทราบผล'}</Chip>
                  : <span className="mr-dim">ยังไม่ได้ตรวจซ้ำ</span>}
                {info.evidence && <div className="mr-ev">ผู้ตรวจเห็นบนหน้าเว็บ: {info.evidence}</div>}
                {!!info.issues.length && <ul className="mr-list">{info.issues.map((x, i) => <li key={i}>{x}</li>)}</ul>}
                {!!info.urls.length && <ul className="mr-list urls">{info.urls.map((u, i) => <li key={i}><SafeLink url={u}/></li>)}</ul>}
              </Field>
            )}

            {(!!(row.flags ?? []).length || !!(row.hs_warnings ?? []).length) && (
              <Field q="มีอะไรต้องระวังไหม?" wide>
                <ul className="mr-list warn">
                  {(row.flags ?? []).map((x, i) => <li key={`f${i}`}>{x}</li>)}
                  {(row.hs_warnings ?? []).map((x, i) => <li key={`h${i}`}>พิกัด: {x}</li>)}
                </ul>
                {!!(row.hs_warnings ?? []).length && <div className="fhint">ระบบไม่เปลี่ยนพิกัดให้ — เตือนอย่างเดียว ชิปปิ้งเป็นคนตัดสิน</div>}
              </Field>
            )}

            {isReview && (
              <Field q="ตอนนี้ในระบบเขียนว่าอะไร?" wide>
                {!groups.length && <span className="mr-dim">ไม่มี SKU</span>}
                <div className="mr-cur">
                  {groups.map((g, i) => (
                    <div className={`mr-curg ${g.differs ? 'diff' : ''}`} key={i}>
                      <div className="mr-curv">{g.missing ? 'หาใบสินค้าไม่เจอ' : g.empty ? 'ยังว่าง' : g.value}</div>
                      <div className="mr-curn">{g.skus.length} SKU{g.differs ? (overwriteHits(g) ? (variants ? ` · จะถูกทับ ${overwriteHits(g)}` : ' · จะถูกทับ') : ' · ต่างจากค่านี้ แต่ยังไม่ได้ติ๊ก') : ''} <span className="mr-skus">{g.skus.slice(0, 6).join(' · ')}{g.skus.length > 6 ? ` · และอีก ${g.skus.length - 6}` : ''}</span></div>
                    </div>
                  ))}
                </div>
              </Field>
            )}

            {status === 'approved' && !!prev.length && (
              <Field q="ก่อนยืนยันเคยเขียนว่าอะไร?" wide>
                <div className="mr-cur">
                  {prev.map((g, i) => (
                    <div className="mr-curg" key={i}>
                      <div className="mr-curv">{g.value || 'ยังว่าง'}</div>
                      <div className="mr-curn">{g.skus.length} SKU <span className="mr-skus">{g.skus.slice(0, 6).join(' · ')}</span></div>
                    </div>
                  ))}
                </div>
              </Field>
            )}

            {isReview && <Field q="ผลิตที่ไหน?" wide><OriginCompare row={row} cards={cards}/></Field>}

            {!!(row.late_skus ?? []).length && status !== 'approved' && (
              <Field q="SKU ที่ AI ไม่ได้ดู?" wide>
                <div className="mr-row"><span className="mr-skus">{row.late_skus.join(' · ')}</span></div>
                <div className="fhint">เข้ามาหลังเริ่มค้นแล้ว — ยืนยันแล้วค่อยเลือกว่าจะใช้ผลนี้กับ SKU พวกนี้ด้วยไหม</div>
              </Field>
            )}

            {status === 'not_found' && (
              <Field q="ค้นที่ไหนไปแล้วบ้าง?" wide>
                {tried.length ? <ul className="mr-list">{tried.map((x, i) => <li key={i}>{/^[a-z][a-z0-9+.-]*:/i.test(x) && !/\s/.test(x) ? <SafeLink url={x}/> : x}</li>)}</ul> : <span className="mr-dim">ไม่มีบันทึก</span>}
              </Field>
            )}

            {retry && (
              <Field q={status === 'researched' ? 'ทำไมยังไม่ตรวจซ้ำ?' : 'ทำไมยังรอคิวอยู่?'} wide>
                ครั้งก่อน{status === 'researched' ? 'ตรวจซ้ำ' : 'ค้น'}ไม่สำเร็จ ระบบเลื่อนไปลองใหม่ {fmtBkk(retry)}{row.backoff_count > 0 ? ` · เลื่อนมาแล้ว ${row.backoff_count} ครั้ง` : ''}
                <div className="fhint">สาเหตุล่าสุด: {thaiOr(row.error, 'ระบบ AI ตอบกลับไม่สำเร็จชั่วคราว')}</div>
              </Field>
            )}

            {has(row.result?.variant_note) && <Field q="สีในรุ่นนี้ต่างกันยังไง?" wide>{row.result.variant_note}</Field>}
            {has(row.notes_th) && <Field q="AI บันทึกอะไรไว้?" wide>{row.notes_th}</Field>}

            {status === 'error' && (
              <Field q="เกิดอะไรขึ้น?" wide>
                {thaiOr(row.error, 'ค้นไม่สำเร็จ — ฝากกด ↻ ค้นใหม่ ถ้ายังไม่ได้ขอให้ช่วยแจ้งแอดมิน')}
                {row.attempts > 0 && <div className="fhint">ลองไปแล้ว {row.attempts} ครั้ง</div>}
              </Field>
            )}
            {/* เหตุผลที่คนพิมพ์ — โชว์ตามที่พิมพ์ อังกฤษก็ได้ (ข้อความระบบดิบไม่ค้างมาถึงตรงนี้: ดู confirmReject) */}
            {status === 'rejected' && has(row.error) && <Field q="ทำไมไม่ใช้?" wide>{row.error}</Field>}
            {status === 'approved' && (
              <Field q="ใครแก้ค่าไหม?" wide>
                {row.approved_edited ? 'คนแก้ค่าก่อนยืนยัน' : 'ยืนยันตามผล AI'}{row.approved_at ? ` · เมื่อ ${fmtBkk(row.approved_at)}` : ''}
              </Field>
            )}
            <Field q="ลงคิวเมื่อไหร่?" wide>{fmtBkk(row.requested_at) || '—'}</Field>
          </div>

          {canAct && status === 'draft' && !editing && !bulkOk && !!blockers?.length && (
            <div className="fhint mr-why">แถวนี้ขอให้ช่วยเปิดดูก่อนยืนยัน เพราะ: {blockers.join(' · ')}</div>
          )}
          {superseded && isReview && <div className="fhint mr-why">รุ่นนี้มีผลใหม่กว่าแล้ว — ฝากช่วยดูที่แถวใหม่ แถวนี้ยืนยันไม่ได้</div>}

          {/* สีวัสดุไม่เท่ากัน → ต้องติ๊กเฉพาะ SKU ที่ตรงกับผลนี้ก่อนยืนยัน (ที่ไม่ติ๊กจะรอไว้ ไม่โดนเขียน) */}
          {canAct && variants && (
            <div className="mr-skupick">
              <div className="mr-q">SKU ไหนบ้างที่ตรงกับผลนี้?</div>
              <div className="fhint">สีในรุ่นนี้วัสดุไม่เท่ากัน ขอให้ช่วยติ๊กเฉพาะ SKU ที่ตรง — ที่ไม่ได้ติ๊กจะยังไม่ถูกเขียน</div>
              <div className="mr-skus-grid">
                {(row.skus ?? []).map(s => (
                  <SkuTick key={s} sku={s} card={cards[s]} next={nextValue} on={picked.includes(s)} disabled={busy || !!ask} onChange={() => onPickSku(row.id, s)}/>
                ))}
              </div>
              <div className="mr-row">
                <button type="button" className="mini" disabled={busy || !!ask} onClick={() => onPickAll(row.id, row.skus ?? [])}>เลือกทุก SKU</button>
                <button type="button" className="mini" disabled={busy || !!ask} onClick={() => onPickAll(row.id, [])}>ล้าง</button>
              </div>
            </div>
          )}

          {/* แถวที่ยืนยันแล้ว: SKU ที่เข้ามาทีหลัง — คนเลือกเองว่าจะใช้ผลนี้ด้วยไหม · เริ่มจากไม่ติ๊กสักตัว
              แถวที่มีผลใหม่กว่าแล้ว = ซ่อน (ค่าเก่าจะไปทับค่าใหม่ และเซิร์ฟเวอร์ก็ไม่รับ) พาไปดูแถวล่าสุดแทน */}
          {status === 'approved' && !!(row.late_skus ?? []).length && superseded && (
            <div className="mr-skupick">
              <div className="mr-q">SKU ที่ยังรอคนเลือก: <span className="mr-skus">{row.late_skus.join(' · ')}</span></div>
              <div className="fhint">รุ่นนี้มีผลใหม่กว่าแล้ว จึงใช้ผลเก่านี้กับ SKU พวกนี้ไม่ได้ — ฝากช่วยดูที่แถวล่าสุด</div>
              {supersededBy && <button type="button" className="btn2 sm" onClick={() => onOpenRow(supersededBy)}>ไปแถวล่าสุด →</button>}
            </div>
          )}
          {status === 'approved' && !!(row.late_skus ?? []).length && !superseded && (
            <div className="mr-skupick">
              <div className="mr-q">ใช้ผลนี้กับ SKU พวกนี้ด้วยไหม?</div>
              <div className="fhint">SKU เหล่านี้ AI ไม่ได้ดู — ขอให้ช่วยติ๊กเฉพาะตัวที่แน่ใจ · ค่าที่ขึ้น “จะถูกทับ” คือค่าที่มีอยู่แล้วและต่างจากผลนี้</div>
              {variants && (
                <div className="mr-warn">AI บอกว่าสีในรุ่นนี้วัสดุไม่เท่ากัน{has(row.result?.variant_note) ? ` — ${row.result.variant_note}` : ''} ผลนี้อาจไม่ตรงกับ SKU พวกนี้ ฝากช่วยเทียบฉลากก่อนติ๊ก</div>
              )}
              <div className="mr-skus-grid">
                {row.late_skus.map(s => (
                  <SkuTick key={s} sku={s} card={cards[s]} next={row.material_th} on={(lateTicks ?? []).includes(s)}
                    disabled={busy || !!ask || !cards[s]} onChange={() => onLateTick(row.id, s)}/>
                ))}
              </div>
              {!ask && (
                <button type="button" className="btn1 sm" disabled={busy || !(lateTicks ?? []).length} onClick={() => onApplyLate(row, lateTicks)}>
                  {busy ? 'กำลังทำ…' : `ใช้ผลนี้กับ ${(lateTicks ?? []).length} SKU`}
                </button>
              )}
            </div>
          )}

          {/* แผงถามก่อนทับค่าเดิม — ขึ้นตอนกดยืนยันเท่านั้น ระหว่างพิมพ์ไม่ขึ้น · ขึ้นแล้วล็อกช่องข้อความ ค่าที่บอกในแผง = ค่าที่จะเขียน */}
          {ask && (
            <div className="mr-ask col" role="alertdialog">
              {ask.changed && <div className="mr-ask-chg">ค่าในระบบเพิ่งเปลี่ยนไปจากที่เห็นบนหน้าจอ — ฝากดูค่าล่าสุดก่อนกดอีกครั้ง</div>}
              <div>{ask.list.length
                ? `ยืนยันแล้วค่านี้จะทับค่าเดิมที่ต่างกัน ${ask.list.length} SKU — ค่าเดิมเก็บไว้ดูย้อนหลังได้ ยืนยันเลยไหม?`
                : 'ตอนนี้ไม่มีค่าเดิมที่จะถูกทับแล้ว ยืนยันเลยไหม?'}</div>
              <div className="mr-ask-v">ค่าที่จะเขียน: {ask.value}</div>
              {!!ask.list.length && (
                <ul className="mr-list">
                  {ask.list.slice(0, 6).map(x => <li key={x.sku}><span className="mr-skus">{x.sku}</span> ตอนนี้เขียนว่า {x.value}</li>)}
                  {ask.list.length > 6 && <li>และอีก {ask.list.length - 6} SKU</li>}
                </ul>
              )}
              <div className="mr-acts">
                <button type="button" className="btn2 sm" disabled={busy} onClick={() => onCancelOverwrite(row.id)}>{editing ? 'กลับไปแก้' : 'ยังไม่ยืนยัน'}</button>
                <button type="button" className="btn1 sm" disabled={busy} onClick={() => onConfirmOverwrite(row)}>{busy ? 'กำลังตรวจค่าล่าสุด…' : ask.list.length ? '✓ ยืนยันทับค่าเดิม' : '✓ ยืนยันเลย'}</button>
              </div>
            </div>
          )}

          {rejectText !== undefined && (
            <div className="mr-ask col">
              <label className="fld"><span className="flabel">ทำไมไม่ใช้ผลนี้? (ไม่บังคับ)</span>
                <input value={rejectText} disabled={busy} onChange={e => onRejectText(row.id, e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }} placeholder="เช่น จะเช็คจากฉลากจริงเอง"/>
              </label>
              <div className="mr-acts">
                <button type="button" className="btn2 sm" disabled={busy} onClick={() => onCancelReject(row.id)}>กลับ</button>
                <button type="button" className="btn-del" disabled={busy} onClick={() => onConfirmReject(row)}>✕ ไม่ใช้ผลนี้</button>
              </div>
            </div>
          )}

          <div className="mr-acts">
            {editing ? (!ask && (
              <>
                <button type="button" className="btn2 sm" disabled={busy} onClick={() => onCancelEdit(row.id)}>ยกเลิกการแก้</button>
                <button type="button" className="btn1 sm" disabled={busy || !has(editing.th) || (variants && !picked.length)} onClick={() => onApprove(row)}>
                  {busy ? 'กำลังตรวจค่าล่าสุด…' : '✓ บันทึกค่านี้แล้วยืนยัน'}
                </button>
              </>
            )) : (
              <>
                {canAct && status === 'draft' && !ask && (
                  <button type="button" className="btn1 sm" disabled={busy || !has(row.material_th) || (variants && !picked.length)}
                    title={variants && !picked.length ? 'ขอให้ช่วยติ๊ก SKU ก่อน' : undefined}
                    onClick={() => onApprove(row)}>
                    {busy ? 'กำลังยืนยัน…' : variants ? `✓ ยืนยันค่านี้ (${picked.length} SKU)` : '✓ ยืนยันค่านี้'}
                  </button>
                )}
                {canAct && <button type="button" className="btn2 sm" disabled={busy} onClick={() => onStartEdit(row)}>✎ {status === 'not_found' ? 'พิมพ์ค่าเองแล้วยืนยัน' : 'แก้แล้วยืนยัน'}</button>}
                {(isReview || ['approved', 'rejected', 'error'].includes(status)) && (
                  <button type="button" className="btn2 sm" disabled={busy} onClick={() => onAgain(row)}>↻ ค้นใหม่</button>
                )}
                {rejectText === undefined && status !== 'approved' && status !== 'rejected' && (
                  <button type="button" className="btn2 sm" disabled={busy} onClick={() => onStartReject(row.id)}>✕ {ACTIVE_STATUSES.includes(status) ? 'ยกเลิกงานนี้' : 'ไม่ใช้ผลนี้'}</button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function MaterialReview({ onOpenProducts, focusId = null, onFocusConsumed }) {
  const { me, mock } = useAuth();
  const isAdmin = mock || !!me?.is_admin;
  const canRun = mock || ['admin', 'coordinator'].includes(me?.role);   // ตรงกับด่าน action "run" ฝั่งเซิร์ฟเวอร์
  const [rows, setRows] = useState(mock ? MOCK_RESEARCH : null);
  const [cards, setCards] = useState(mock ? MOCK_CARDS : {});
  const [control, setControl] = useState(mock ? MOCK_CONTROL : null);
  const [spent, setSpent] = useState(mock ? MOCK_SPENT : null);
  const [phase, setPhase] = useState(mock ? 'ready' : 'loading');   // loading | ready | notready | error
  const [loadErr, setLoadErr] = useState(null);
  const [pollFail, setPollFail] = useState(false);                  // รีเฟรชเบื้องหลังพลาด — ของเดิมยังโชว์อยู่
  const [updatedAt, setUpdatedAt] = useState(null);
  const [section, setSection] = useState(null);                     // null = เลือกให้เองตามงานที่มี
  const [openIds, setOpenIds] = useState(() => new Set());
  const [edits, setEdits] = useState({});                           // id → { th, en } — อยู่นอกรายการ รีเฟรชไม่ทับ
  const [skuPicks, setSkuPicks] = useState({});                     // id → [sku] (สีวัสดุต่างกัน)
  const [lateTicks, setLateTicks] = useState({});                   // id → [sku] (แถวยืนยันแล้ว)
  const [rejects, setRejects] = useState({});                       // id → เหตุผลที่กำลังพิมพ์
  const [overwriteAsk, setOverwriteAsk] = useState(null);           // { id, kind: approve|apply, value, list, key, targets, changed } แผงถามก่อนทับค่าเดิม
  const [flashId, setFlashId] = useState(null);                     // แถวที่เพิ่งพาไปดู (ไฮไลต์สั้น ๆ)
  const [bulk, setBulk] = useState(() => new Set());
  const [brokenCards, setBrokenCards] = useState(() => new Set());   // การ์ดที่แสดงไม่ได้ (CardBoundary)
  const markBroken = (id, broken) => setBrokenCards(p => {
    if (p.has(id) === broken) return p;
    const n = new Set(p);
    broken ? n.add(id) : n.delete(id);
    return n;
  });
  const [bulkAsk, setBulkAsk] = useState(false);
  const [busyId, setBusyId] = useState(null);                       // id | 'bulk' | 'control'
  const [bulkMsg, setBulkMsg] = useState(null);
  const [note, setNote] = useState(null);                           // { kind: 'ok'|'err', text }
  const [after, setAfter] = useState(null);                         // หลังยืนยัน: SKU ที่ควรถามต่อ
  const [again, setAgain] = useState(null);                         // { row, ids } — หน้าต่างค้นใหม่
  const [cancelAsk, setCancelAsk] = useState(false);
  const [capDraft, setCapDraft] = useState(null);                   // แอดมินกำลังปรับเพดาน
  const alive = useRef(true);
  const reqRef = useRef(0);
  const busyRef = useRef(false);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const focusPending = useRef(focusId);                             // แถวที่ลิงก์ชี้มา รอรายการโหลดเสร็จ
  const pinned = useRef(new Set());                                 // แถวเก่าเกิน 500 ล่าสุดที่ลิงก์ชี้มา — โหลดเพิ่มให้ทุกรอบ

  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const load = async ({ quiet = false } = {}) => {
    if (mock) { setUpdatedAt(new Date().toISOString()); return; }
    const my = ++reqRef.current;
    try {
      const [list, ctl, sp] = await Promise.all([
        db.listMaterialResearch(),
        db.getMaterialControl(),
        db.materialSpend24h().catch(() => null),   // ยอดใช้โหลดไม่ขึ้นไม่ควรทำให้ทั้งหน้าพัง
      ]);
      for (const id of pinned.current) {
        if (list.some(r => r.id === id)) continue;
        const one = await db.getMaterialResearch(id).catch(() => null);   // id มาจาก URL พิมพ์เองได้ — พัง = ไม่พบ
        if (one) list.push(one);
      }
      const pendingSkus = list.flatMap(skusNeedingCards);
      const cs = pendingSkus.length ? await db.listCardsBySkus(pendingSkus) : [];
      if (!alive.current || my !== reqRef.current) return;
      setRows(list); setCards(toCardMap(cs)); setControl(ctl); setSpent(sp);
      setPhase('ready'); setPollFail(false); setLoadErr(null);
      setUpdatedAt(new Date().toISOString());
    } catch (e) {
      if (!alive.current || my !== reqRef.current) return;
      if (e?.notReady) { setPhase('notready'); return; }
      // รีเฟรชเบื้องหลังพลาด = ของเดิมยังใช้ได้ ไม่ทำลายหน้าที่คนกำลังตรวจอยู่
      if (quiet && rowsRef.current) { setPollFail(true); return; }
      setLoadErr(e?.message ?? 'โหลดรายการหาวัสดุไม่สำเร็จ — ฝากลองใหม่อีกครั้ง');
      setPhase('error');
    }
  };
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => { loadRef.current(); }, []);

  // รีเฟรชทุก 60 วิ เฉพาะตอนมีงานกำลังเดินและเปิดหน้านี้อยู่ · กลับมาที่แท็บหลังหายไปนาน = โหลดทันที
  const hasActive = (rows ?? []).some(r => ACTIVE_STATUSES.includes(r.status));
  useEffect(() => {
    if (mock || !hasActive) return undefined;
    let last = Date.now();
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      last = Date.now();
      loadRef.current({ quiet: true });
    };
    const t = setInterval(tick, 60000);
    const onVisible = () => { if (document.visibilityState === 'visible' && Date.now() - last >= 60000) tick(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVisible); };
  }, [mock, hasActive]);

  // พาไปที่แถว: เปิดส่วนที่แถวนั้นอยู่ · กางการ์ด · เลื่อนมาให้เห็น
  const focusRow = (r) => {
    const sec = SECTIONS.find(x => x.statuses.includes(r.status));
    if (sec) setSection(sec.id);
    setOpenIds(p => new Set(p).add(r.id));
    setFlashId(r.id);
  };
  useEffect(() => {
    if (!flashId) return undefined;
    document.getElementById(`mr-row-${flashId}`)?.scrollIntoView({ block: 'start' });
    const t = setTimeout(() => setFlashId(null), 2600);
    return () => clearTimeout(t);
  }, [flashId]);
  // แถวที่ถูกชี้มา (ลิงก์จากหน้าต่างลงคิว / URL) — ไม่อยู่ในรายการ = อาจเก่าเกิน 500 แถวล่าสุด ลองโหลดแถวนั้นเพิ่มก่อนบอกว่าไม่พบ
  useEffect(() => {
    const id = focusPending.current;
    if (!id || phase !== 'ready' || !rows) return;
    const r = rows.find(x => x.id === id);
    if (r) { focusPending.current = null; focusRow(r); onFocusConsumed?.(true); return; }
    if (mock || pinned.current.has(id)) { focusPending.current = null; onFocusConsumed?.(false); return; }
    pinned.current.add(id);
    loadRef.current({ quiet: true });
  }, [phase, rows]);
  const openRowById = (id) => {
    if (!id) return;
    const r = (rowsRef.current ?? []).find(x => x.id === id);
    if (r) { focusRow(r); return; }
    focusPending.current = id;
    pinned.current.add(id);
    loadRef.current({ quiet: true });
  };

  const editDirty = Object.entries(edits).some(([id, e]) => {
    const r = (rows ?? []).find(x => x.id === id);
    return r && (r.status === 'draft' || r.status === 'not_found') && (!sameText(e.th, r.material_th) || !sameText(e.en, r.material_en));
  });
  const rejectDirty = Object.values(rejects).some(v => has(v));
  useLeaveGuard(busyId ? 'กำลังทำรายการอยู่ — ออกตอนนี้อาจไม่ครบ ออกเลยไหม?'
    : editDirty || rejectDirty ? 'ยังไม่ได้ยืนยันค่าที่แก้ไว้ในหน้าตรวจผลวัสดุ — ออกจากหน้านี้เลยไหม? (ที่แก้ไว้จะหาย)' : null);

  // ── ตัวช่วย state — เขียนแบบ prev => … เสมอ (คำตอบหลัง await ห้ามทับสิ่งที่คนทำระหว่างรอ) ──
  const clearRowState = (id) => {
    setEdits(p => { const n = { ...p }; delete n[id]; return n; });
    setSkuPicks(p => { const n = { ...p }; delete n[id]; return n; });
    setRejects(p => { const n = { ...p }; delete n[id]; return n; });
    setBulk(p => { const n = new Set(p); n.delete(id); return n; });
    setOverwriteAsk(a => (a?.id === id ? null : a));
  };
  const clearAsk = (id) => setOverwriteAsk(a => (a?.id === id ? null : a));
  const mockPatch = (id, patch) => setRows(p => p.map(r => (r.id === id ? { ...r, ...patch } : r)));
  // แถวล็อก: กดซ้ำ/กดแถวอื่นระหว่างรอไม่ได้
  const guarded = async (key, fn) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusyId(key); setNote(null);
    try { await fn(); }
    catch (e) { if (alive.current) { setOverwriteAsk(x => (x?.id === key ? null : x)); setNote({ kind: 'err', text: e?.message ?? 'ทำรายการไม่สำเร็จ — ฝากโหลดหน้าใหม่แล้วลองอีกครั้ง' }); await loadRef.current({ quiet: true }); } }
    finally { busyRef.current = false; if (alive.current) setBusyId(null); }
  };

  // ยืนยันหนึ่งแถว (ใช้ร่วมทั้ง ✓ และ ✎) → บอกผล + ถามต่อเรื่อง SKU ที่เหลือ
  const reportApproved = (row, res) => {
    const updated = Number(res?.updated ?? 0);
    const missing = res?.missing_skus ?? [];
    const late = res?.late_skus ?? [];
    setNote({
      kind: missing.length ? 'err' : 'ok',
      text: `ยืนยัน ${row.model_code} แล้ว — เขียนลงสินค้า ${updated} รายการ${missing.length ? ` · ไม่พบ SKU ${missing.join(', ')} (อาจเปลี่ยนรหัสไปแล้ว ฝากช่วยตรวจ)` : ''}`,
    });
    if (late.length) setAfter({ id: row.id, model: row.model_code, late });
  };

  // ด่านก่อนเขียนลงสินค้าทีละแถว — ทุกทาง (✓ · ✎ · พิมพ์เอง · ใช้กับ SKU เพิ่ม · ปุ่มในแผงถาม) ผ่านที่นี่
  //   อ่านค่าล่าสุดก่อนเสมอ แล้วเทียบกับ "ภาพที่คนเห็น" (แผงที่เปิดอยู่ ถ้าไม่มี = ค่าบนหน้าจอ)
  //   ต่างจากที่เห็น หรือจะทับค่าเดิมแต่ยังไม่ได้ถาม = เปิดแผงถาม ไม่เขียน · อ่านไม่ได้ = โยน error ไม่เขียน
  const passOverwriteGate = async (row, kind, targets, value) => {
    const asked = overwriteAsk?.id === row.id && overwriteAsk.kind === kind ? overwriteAsk : null;
    const shown = asked?.key ?? overwriteKey(value, overwriteList(targets, cards, value));
    const fresh = mock ? cards : toCardMap(await db.listCardsBySkus(targets));
    if (!alive.current) return false;
    if (!mock) setCards(p => {
      const n = { ...p };
      for (const s of targets) { if (fresh[s]) n[s] = fresh[s]; else delete n[s]; }
      return n;
    });
    const list = overwriteList(targets, fresh, value);
    const key = overwriteKey(value, list);
    if (key === shown && (asked || !list.length)) return true;
    setOverwriteAsk({ id: row.id, kind, value, list, key, targets, changed: key !== shown });
    return false;
  };

  const approve = (row) => guarded(row.id, async () => {
    const variants = variantsDiffer(row);
    const picked = skuPicks[row.id] ?? [];
    if (variants && !picked.length) throw new Error('ขอให้ช่วยติ๊ก SKU ที่ตรงกับผลนี้ก่อนยืนยัน');
    const { args, value } = approvePlan(row, edits[row.id]);
    if (!has(value)) throw new Error('ยังไม่มีค่าวัสดุให้ยืนยัน — ฝากช่วยพิมพ์ค่าที่ตรวจแล้วก่อน');
    if (!await passOverwriteGate(row, 'approve', variants ? picked : (row.skus ?? []), value)) return;
    const call = { ...args, ...(variants ? { skus: picked } : {}) };
    let res;
    if (mock) {
      const skus = variants ? picked : row.skus;
      const late = [...(row.late_skus ?? []), ...(variants ? row.skus.filter(s => !picked.includes(s)) : [])];
      const th = args.materialTh ?? row.material_th;
      mockPatch(row.id, {
        status: 'approved', material_th: th, material_en: args.materialTh !== undefined && args.materialEn === undefined ? null : (args.materialEn ?? row.material_en),
        approved_at: new Date().toISOString(), approved_edited: args.materialTh !== undefined || args.materialEn !== undefined,
        skus, late_skus: late,
        source_url: row.status === 'not_found' ? null : row.source_url,
        prev_values: Object.fromEntries(skus.map(s => [s, { material: cards[s]?.material ?? '', material_en: null, material_source_url: null }])),
      });
      setCards(p => ({ ...p, ...Object.fromEntries(skus.filter(s => p[s]).map(s => [s, { ...p[s], material: th }])) }));
      res = { updated: skus.length, missing_skus: [], late_skus: late };
    } else {
      res = await db.approveMaterial(row.id, call);
    }
    if (!alive.current) return;
    clearRowState(row.id);
    reportApproved(row, res);
    await loadRef.current({ quiet: true });
  });

  const applyLate = (row, skus) => guarded(row.id, async () => {
    if (!await passOverwriteGate(row, 'apply', skus, row.material_th)) return;
    const res = mock
      ? { updated: skus, skipped: [] }
      : await db.applyMaterial(row.id, skus);
    if (mock) {
      mockPatch(row.id, { skus: [...row.skus, ...skus], late_skus: row.late_skus.filter(s => !skus.includes(s)) });
      setCards(p => ({ ...p, ...Object.fromEntries(skus.filter(s => p[s]).map(s => [s, { ...p[s], material: row.material_th }])) }));
    }
    if (!alive.current) return;
    clearAsk(row.id);
    setLateTicks(p => { const n = { ...p }; delete n[row.id]; return n; });
    setAfter(a => (a?.id === row.id ? null : a));
    const skipped = res?.skipped?.length ?? 0;
    setNote({ kind: skipped ? 'err' : 'ok', text: `ใช้ผล ${row.model_code} กับ ${res?.updated?.length ?? 0} SKU แล้ว${skipped ? ` · ข้าม ${skipped} SKU (แบรนด์ไม่ตรง หรือไม่ได้รออยู่ในงานนี้)` : ''}` });
    await loadRef.current({ quiet: true });
  });

  const confirmReject = (row) => guarded(row.id, async () => {
    // ⚠ ส่งเหตุผลเสมอ — ไม่ส่ง = เซิร์ฟเวอร์เก็บข้อความในช่อง error ไว้ ซึ่งอาจเป็นข้อความระบบดิบที่ยังไม่ขึ้นจอนี้
    //   (งานเดินอยู่ เขียน error หลังโหลดรอบล่าสุด) แล้วแถวไม่ใช้โชว์ตามตัวอักษร ข้อความดิบจะไปถึงพนักงานเอง
    const reason = String(rejects[row.id] ?? '').trim()
      || (has(row.error) ? thaiOr(row.error, '') || 'ไม่ใช้ผลนี้ หลังค้นไม่สำเร็จ' : 'ไม่ได้ระบุเหตุผล');
    if (mock) mockPatch(row.id, { status: 'rejected', error: reason, finished_at: new Date().toISOString() });
    else await db.rejectMaterial(row.id, reason);
    if (!alive.current) return;
    clearRowState(row.id);
    setNote({ kind: 'ok', text: `${row.model_code}: บันทึกว่าไม่ใช้ผลนี้แล้ว` });
    await loadRef.current({ quiet: true });
  });

  // ↻ ค้นใหม่ = ไม่ใช้ผลเดิม แล้วลงคิวแบบ force ด้วย id ของ skus ∪ late_skus — ผ่านหน้า "ดูก่อนลงคิว" เสมอ (เสียค่าค้นอีกรอบ)
  const startAgain = (row) => guarded(row.id, async () => {
    const list = skuUnion(row);
    const cs = mock ? list.map(s => MOCK_CARDS[s] ?? { id: `mock-${s}`, sku: s }) : await db.listCardsBySkus(list);
    if (!alive.current) return;
    if (!cs.length) throw new Error(`หาใบสินค้าของ ${row.model_code} ไม่เจอ — อาจเปลี่ยนรหัสไปแล้ว`);
    clearAsk(row.id);
    setAgain({ row, ids: cs.map(c => c.id) });
  });

  // ── ยืนยันหลายแถว ──
  const reviewRows = (rows ?? []).filter(r => r.status === 'draft' || r.status === 'not_found');
  const superseded = supersededMap(rows ?? []);
  const newerApproved = newerApprovedMap(rows ?? []);   // แถวที่ยืนยันแล้วถูกแทนเมื่อมีแถวที่ยืนยันใหม่กว่าเท่านั้น (ตรงกับเซิร์ฟเวอร์)
  // แถวที่กำลังแก้ค่าอยู่ไม่เข้าชุดยืนยันพร้อมกัน — ชุดนี้เขียนผล AI ตรง ๆ จะทิ้งค่าที่คนกำลังแก้
  // การ์ดที่แสดงไม่ได้ = คนไม่เห็นค่า ห้ามเข้าชุดยืนยันพร้อมกัน
  const eligible = reviewRows.filter(r => !superseded.has(r.id) && !edits[r.id] && !brokenCards.has(r.id) && canBulkApprove(r, cards));
  const bulkIds = eligible.filter(r => bulk.has(r.id));
  const bulkSkuTotal = bulkIds.reduce((s, r) => s + (r.skus ?? []).length, 0);
  const runBulk = () => guarded('bulk', async () => {
    setBulkAsk(false);
    const list = bulkIds;
    let done = 0, skipped = 0, failed = 0;
    let firstErr = null;
    let late = 0;
    // ⚠ ตรวจกับค่าล่าสุดอีกรอบก่อนเขียน — ระหว่างเปิดหน้านี้อาจมีคนกรอกวัสดุไปแล้ว ห้ามทับเงียบ ๆ
    const fresh = mock ? cards : toCardMap(await db.listCardsBySkus(list.flatMap(skuUnion)));
    for (let i = 0; i < list.length; i++) {
      const r = list[i];
      setBulkMsg(`กำลังยืนยัน ${i + 1}/${list.length}…`);
      if (!canBulkApprove(r, fresh)) { skipped++; continue; }
      try {
        if (mock) {
          mockPatch(r.id, { status: 'approved', approved_at: new Date().toISOString(), approved_edited: false,
            prev_values: Object.fromEntries(r.skus.map(s => [s, { material: fresh[s]?.material ?? '', material_en: null, material_source_url: null }])) });
          setCards(p => ({ ...p, ...Object.fromEntries(r.skus.filter(s => p[s]).map(s => [s, { ...p[s], material: r.material_th }])) }));
        } else {
          const res = await db.approveMaterial(r.id);
          late += res?.late_skus?.length ?? 0;
        }
        done++;
      } catch (e) { failed++; firstErr ??= e?.message; }
    }
    if (!alive.current) return;
    setBulkMsg(null);
    setBulk(new Set());
    const parts = [`ยืนยันแล้ว ${done} แถว`];
    if (skipped) parts.push(`ข้าม ${skipped} แถวเพราะค่าเปลี่ยนไปแล้ว ขอให้ช่วยเปิดดูทีละแถว`);
    if (failed) parts.push(`ไม่สำเร็จ ${failed} แถว${firstErr ? ` (${firstErr})` : ''}`);
    if (late) parts.push(`มี SKU ที่ควรดูต่อในส่วน “ยืนยันแล้ว-ไม่ใช้” ${late} รายการ`);
    setNote({ kind: failed || skipped ? 'err' : 'ok', text: parts.join(' · ') });
    await loadRef.current({ quiet: true });
  });

  // ── แถบควบคุม ──
  // เดินคิวเดี๋ยวนี้ — ก่อนมี cron และตอนไม่อยากรอรอบถัดไป · รอบที่ระบบไม่รับงานต้องบอกตรง ๆ จะได้ไม่นึกว่าเริ่มแล้ว
  const runNow = () => guarded('control', async () => {
    const res = mock ? { processed: 1 } : await db.runMaterialQueue();
    if (!alive.current) return;
    const n = Number(res?.processed);
    if (res?.skipped) setNote({ kind: 'err', text: 'ระบบยังไม่พร้อมรับงานรอบนี้ ฝากกดอีกครั้งในอีก 1 นาที' });
    else if (res?.processed != null && Number.isFinite(n) && n <= 0) setNote({ kind: 'err', text: 'รอบนี้ไม่มีงานที่เริ่มได้ — งานอาจรอเวลาลองใหม่อยู่ หรือยอดใช้ถึงเพดานแล้ว' });
    else setNote({ kind: 'ok', text: `เริ่มเดินคิวแล้ว${Number.isFinite(n) && n > 0 ? ` ${n} งาน` : ''} — ผลจะขึ้นเองเมื่อเสร็จ` });
    await loadRef.current({ quiet: true });
  });
  const setPaused = (paused) => guarded('control', async () => {
    if (mock) setControl(c => ({ ...c, paused, paused_reason: null }));
    else await db.setMaterialControl({ paused });
    if (!alive.current) return;
    setNote({ kind: 'ok', text: paused ? 'พักคิวแล้ว — งานที่กำลังค้นอยู่จะทำจนจบรอบ งานที่รอคิวยังไม่เริ่ม' : 'เดินคิวต่อแล้ว' });
    await loadRef.current({ quiet: true });
  });
  const cancelAll = () => guarded('control', async () => {
    setCancelAsk(false);
    let n;
    if (mock) {
      n = rows.filter(r => r.status === 'queued').length;
      setRows(p => p.map(r => (r.status === 'queued' ? { ...r, status: 'rejected', error: 'ยกเลิกทั้งคิว', finished_at: new Date().toISOString() } : r)));
    } else {
      n = await db.cancelQueuedMaterials();
    }
    if (!alive.current) return;
    setNote({ kind: 'ok', text: `ยกเลิกคิวแล้ว ${Number(n ?? 0)} รุ่น` });
    await loadRef.current({ quiet: true });
  });
  const saveCaps = () => guarded('control', async () => {
    const daily = Number(capDraft?.daily), click = Number(capDraft?.click);
    if (!Number.isFinite(daily) || daily <= 0) throw new Error('ฝากช่วยใส่เพดานต่อวันเป็นตัวเลขมากกว่า 0');
    if (!Number.isInteger(click) || click < 1) throw new Error('ฝากช่วยใส่เพดานต่อครั้งเป็นจำนวนเต็มตั้งแต่ 1 ขึ้นไป');
    if (mock) setControl(c => ({ ...c, daily_max_usd: daily, per_click_max: click }));
    else await db.setMaterialControl({ dailyMaxUsd: daily, perClickMax: click });
    if (!alive.current) return;
    setCapDraft(null);
    setNote({ kind: 'ok', text: 'บันทึกเพดานแล้ว' });
    await loadRef.current({ quiet: true });
  });

  // ── render ──
  if (phase === 'loading') return <div className="placeholder"><h2>กำลังโหลด…</h2></div>;
  if (phase === 'notready') return (
    <div className="placeholder">
      <h2>ยังไม่พร้อมใช้งาน</h2>
      <p>ระบบหาวัสดุด้วย AI ยังตั้งค่าไม่เสร็จ — ฝากแจ้งแอดมินให้ช่วยตั้งค่าให้เสร็จก่อน</p>
      <button className="tab" onClick={() => { setPhase('loading'); load(); }}>ลองใหม่</button>
    </div>
  );
  if (phase === 'error') return (
    <div className="placeholder">
      <h2>โหลดไม่สำเร็จ</h2>
      <p>{loadErr}</p>
      <button className="tab" onClick={() => { setPhase('loading'); load(); }}>ลองใหม่</button>
    </div>
  );

  const list = rows ?? [];
  const inSection = (s) => list.filter(r => s.statuses.includes(r.status));
  // ขัดข้องที่ค้นใหม่ไปแล้ว ยังอยู่ในส่วนนี้เพื่อเก็บข้อความเดิม แต่ไม่นับเป็นงานค้าง
  const sectionCount = (s) => (s.id === 'error' ? inSection(s).filter(r => !superseded.has(r.id)).length : inSection(s).length);
  // ค่าเริ่ม = ส่วนแรกที่มีงานให้ทำ (รอตรวจก่อน) ไม่ขึ้นหน้าว่างทั้งที่มีงานอยู่
  const currentSection = SECTIONS.find(s => s.id === section)
    ?? SECTIONS.find(s => inSection(s).length) ?? SECTIONS[0];
  const shown = inSection(currentSection);
  const queuedN = list.filter(r => r.status === 'queued').length;
  const researchedN = list.filter(r => r.status === 'researched').length;
  const paused = !!control?.paused;
  const overDaily = control && spent != null && Number(control.daily_max_usd) > 0 && spent >= Number(control.daily_max_usd);

  return (
    <div className="page mr">
      <div className="page-h">
        <h2>วัสดุจาก AI</h2>
        <span className="meta">AI ค้นวัสดุจากเว็บผู้ผลิต — ยังไม่เขียนลงสินค้าจนกว่าคนจะกดยืนยันที่หน้านี้</span>
        <span className="mr-h-r">
          {updatedAt && <span className="mr-updated">อัปเดต {fmtBkk(updatedAt)}{hasActive && !mock ? ' · รีเฟรชเองทุก 60 วินาที' : ''}</span>}
          <button type="button" className="mini" disabled={!!busyId} onClick={() => load()}>↻ โหลดใหม่</button>
        </span>
      </div>

      {mock && <div className="importmsg">โหมด MOCK — ข้อมูลตัวอย่างครบทุกสถานะ กดปุ่มได้แต่ไม่เขียนฐานข้อมูล</div>}
      {pollFail && <div className="dossier-warn">รีเฟรชล่าสุดไม่สำเร็จ — ที่เห็นอยู่อาจไม่ใช่ข้อมูลล่าสุด ฝากกด ↻ โหลดใหม่</div>}
      {note && (
        <div className={note.kind === 'err' ? 'ferr mr-note' : 'pp-ok mr-note'} role="status">
          <span>{note.text}</span>
          <button type="button" className="navnote-x" onClick={() => setNote(null)} title="ปิด">✕</button>
        </div>
      )}
      {after && (
        <div className="mr-ask" role="alertdialog">
          <span>{after.model}: มี SKU ที่ AI ไม่ได้ดู {after.late.length} รายการ ({after.late.slice(0, 4).join(' · ')}{after.late.length > 4 ? ' …' : ''}) — ใช้ผลนี้กับ SKU พวกนี้ด้วยไหม?</span>
          <button type="button" className="btn2 sm" onClick={() => setAfter(null)}>ยังไม่ใช้</button>
          <button type="button" className="btn1 sm" onClick={() => {
            setSection('done');
            setOpenIds(p => new Set(p).add(after.id));
            setFlashId(after.id);
            setAfter(null);
          }}>ไปเลือก SKU →</button>
        </div>
      )}

      {/* แถบควบคุมคิว */}
      <div className="mr-ctl">
        <div className="mr-ctl-i">
          <span className="mr-q">คิวเดินอยู่ไหม?</span>
          <span className={`mr-ctl-v ${paused ? 'off' : 'on'}`}>{paused ? '● พักอยู่' : '● เดินอยู่'}</span>
          {paused && <span className="mr-ctl-why">{thaiOr(control?.paused_reason, 'กดเดินคิวต่อได้เมื่อพร้อม')}</span>}
          <button type="button" className="btn2 sm" disabled={!!busyId} onClick={() => setPaused(!paused)}>
            {paused ? 'เดินคิวต่อ' : 'พักคิว'}
          </button>
        </div>
        <div className="mr-ctl-i">
          <span className="mr-q">ใช้ไปแล้วใน 24 ชม.?</span>
          <span className={`mr-ctl-v ${overDaily ? 'off' : ''}`}>{fmtUsd(spent)} / {fmtUsd(control?.daily_max_usd)}</span>
          {overDaily && <span className="mr-ctl-why">ถึงเพดานแล้ว งานจะรอจนกว่ายอดจะลดลง</span>}
        </div>
        <div className="mr-ctl-i">
          <span className="mr-q">รอคิวอยู่กี่รุ่น?</span>
          <span className="mr-ctl-v">{queuedN}{researchedN ? ` · รอตรวจซ้ำ ${researchedN}` : ''}</span>
          {canRun && (queuedN > 0 || researchedN > 0) && (
            <button type="button" className="btn2 sm" disabled={!!busyId || paused} title={paused ? 'พักคิวอยู่ — ฝากกดเดินคิวต่อก่อน' : 'ไม่ต้องรอรอบถัดไป ให้ระบบเริ่มงานเดี๋ยวนี้'} onClick={runNow}>เดินคิวตอนนี้</button>
          )}
          {!cancelAsk
            ? <button type="button" className="btn2 sm" disabled={!!busyId || !queuedN} onClick={() => setCancelAsk(true)}>ยกเลิกคิวทั้งหมด</button>
            : <span className="mr-inline">
                ยกเลิกทั้ง {queuedN} รุ่นเลยไหม?
                <button type="button" className="btn2 sm" onClick={() => setCancelAsk(false)}>ไม่ยกเลิก</button>
                <button type="button" className="btn-del" disabled={!!busyId} onClick={cancelAll}>ยกเลิกทั้งคิว</button>
              </span>}
        </div>
        <div className="mr-ctl-i">
          <span className="mr-q">เพดานต่อครั้งกี่รุ่น?</span>
          <span className="mr-ctl-v">{control?.per_click_max ?? '—'}</span>
          {isAdmin && !capDraft && (
            <button type="button" className="btn2 sm" disabled={!!busyId}
              onClick={() => setCapDraft({ daily: String(control?.daily_max_usd ?? ''), click: String(control?.per_click_max ?? '') })}>ปรับเพดาน</button>
          )}
        </div>
        {capDraft && (
          <div className="mr-capedit">
            <label className="fld"><span className="flabel">ต่อวันไม่เกินกี่ดอลลาร์?</span>
              <input inputMode="decimal" value={capDraft.daily} onChange={e => { const v = e.target.value; setCapDraft(d => ({ ...d, daily: v })); }}
                onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}/></label>
            <label className="fld"><span className="flabel">ต่อครั้งไม่เกินกี่รุ่น?</span>
              <input inputMode="numeric" value={capDraft.click} onChange={e => { const v = e.target.value; setCapDraft(d => ({ ...d, click: v })); }}
                onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}/></label>
            <span className="mr-acts">
              <button type="button" className="btn2 sm" disabled={!!busyId} onClick={() => setCapDraft(null)}>ยกเลิก</button>
              <button type="button" className="btn1 sm" disabled={!!busyId} onClick={saveCaps}>บันทึกเพดาน</button>
            </span>
          </div>
        )}
      </div>

      <div className="mr-secs" role="tablist">
        {SECTIONS.map(s => (
          <button type="button" key={s.id} role="tab" aria-selected={currentSection.id === s.id}
            className={`mr-sec ${currentSection.id === s.id ? 'on' : ''}`} onClick={() => setSection(s.id)}>
            {s.label} <b>{sectionCount(s)}</b>
          </button>
        ))}
      </div>

      {currentSection.id === 'review' && (
        <div className="mr-bulk">
          <span className="pp-cnt">ยืนยันพร้อมกันได้ <b>{eligible.length}</b> แถว</span>
          {eligible.length > 0 && <>
            <button type="button" className="mini" disabled={!!busyId} onClick={() => setBulk(new Set(eligible.map(r => r.id)))}>เลือกทุกแถวที่ยืนยันพร้อมกันได้</button>
            <button type="button" className="mini" disabled={!!busyId || !bulkIds.length} onClick={() => { setBulk(new Set()); setBulkAsk(false); }}>ล้าง</button>
            {!bulkAsk
              ? <button type="button" className="btn1 sm" disabled={!!busyId || !bulkIds.length} onClick={() => setBulkAsk(true)}>✓ ยืนยัน {bulkIds.length} แถวที่เลือก</button>
              : <span className="mr-inline">
                  เขียนลงสินค้ารวม {bulkSkuTotal} SKU เลยไหม?
                  <button type="button" className="btn2 sm" onClick={() => setBulkAsk(false)}>ยังไม่ยืนยัน</button>
                  <button type="button" className="btn1 sm" disabled={!!busyId} onClick={runBulk}>✓ ยืนยันเลย</button>
                </span>}
          </>}
          {bulkMsg && <span className="mr-updated">{bulkMsg}</span>}
          <div className="fhint mr-bulk-why">
            ยืนยันพร้อมกันได้เฉพาะแถวที่มั่นใจสูง · ตรวจซ้ำตรงกับหน้าเว็บ · มาจากเว็บทางการที่อยู่ในทะเบียน · ไม่มีธงเตือน · ค่าเดิมว่างหรือตรงกันอยู่แล้ว — แถวอื่นขอให้ช่วยเปิดดูทีละแถว
          </div>
        </div>
      )}

      {shown.map(r => (
        <CardBoundary key={`${r.id}|${r.status}`} row={r} onFail={markBroken}>
        <ResearchCard row={r} cards={cards}
          open={openIds.has(r.id)}
          superseded={(r.status === 'approved' ? newerApproved : superseded).has(r.id)}
          supersededBy={(r.status === 'approved' ? newerApproved : superseded).get(r.id) ?? null}
          flash={flashId === r.id}
          busy={busyId === r.id || busyId === 'bulk'}
          editing={r.status === 'draft' || r.status === 'not_found' ? edits[r.id] ?? null : null}
          pickedSkus={skuPicks[r.id]}
          lateTicks={lateTicks[r.id]}
          rejectText={rejects[r.id]}
          ask={overwriteAsk?.id === r.id ? overwriteAsk : null}
          bulkOk={eligible.some(x => x.id === r.id)}
          bulkTicked={bulk.has(r.id)}
          blockers={r.status === 'draft' ? bulkBlockers(r, cards) : null}
          onToggle={(id) => setOpenIds(p => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; })}
          onBulkTick={(id) => setBulk(p => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; })}
          onApprove={approve}
          onStartEdit={(row) => { clearAsk(row.id); setEdits(p => ({ ...p, [row.id]: { th: row.material_th ?? '', en: row.material_en ?? '' } })); }}
          onEditChange={(id, patch) => { clearAsk(id); setEdits(p => ({ ...p, [id]: { ...p[id], ...patch } })); }}
          onCancelEdit={(id) => { clearAsk(id); setEdits(p => { const n = { ...p }; delete n[id]; return n; }); }}
          onAgain={startAgain}
          onStartReject={(id) => { clearAsk(id); setRejects(p => ({ ...p, [id]: '' })); }}
          onRejectText={(id, v) => setRejects(p => ({ ...p, [id]: v }))}
          onCancelReject={(id) => setRejects(p => { const n = { ...p }; delete n[id]; return n; })}
          onConfirmReject={confirmReject}
          onPickSku={(id, s) => { clearAsk(id); setSkuPicks(p => { const c = p[id] ?? []; return { ...p, [id]: c.includes(s) ? c.filter(x => x !== s) : [...c, s] }; }); }}
          onPickAll={(id, skus) => { clearAsk(id); setSkuPicks(p => ({ ...p, [id]: skus })); }}
          onCancelOverwrite={clearAsk}
          onConfirmOverwrite={(row) => (overwriteAsk?.kind === 'apply' ? applyLate(row, overwriteAsk.targets) : approve(row))}
          onLateTick={(id, s) => { clearAsk(id); setLateTicks(p => { const c = p[id] ?? []; return { ...p, [id]: c.includes(s) ? c.filter(x => x !== s) : [...c, s] }; }); }}
          onApplyLate={applyLate}
          onOpenRow={openRowById}/>
        </CardBoundary>
      ))}
      {!shown.length && (
        <div className="placeholder mr-empty">
          <h2>{currentSection.id === 'review' ? 'ยังไม่มีงานรอตรวจ'
            : currentSection.id === 'active' ? 'ไม่มีงานที่กำลังค้นอยู่'
            : currentSection.id === 'error' ? 'ไม่มีงานที่ขัดข้อง' : 'ยังไม่มีงานที่ยืนยันหรือไม่ใช้'}</h2>
          {currentSection.id === 'review' && (
            <p>เลือกสินค้าที่แท็บ “สินค้า &amp; HS” แล้วกด “หาวัสดุด้วย AI” — ผลจะขึ้นที่นี่ให้ตรวจก่อนลงสินค้า
              {onOpenProducts && <><br/><button type="button" className="tab" onClick={onOpenProducts}>ไปหน้าสินค้า &amp; HS →</button></>}</p>
          )}
        </div>
      )}
      {currentSection.id === 'done' && inSection(currentSection).length >= db.MAT_DONE_CAP && (
        <div className="fhint mr-cap">ส่วนนี้แสดงเฉพาะ {db.MAT_DONE_CAP} งานล่าสุด — งานที่ยังไม่จบในส่วนอื่นแสดงครบทุกงาน</div>
      )}

      {again && (
        <MaterialEnqueueModal ids={again.ids} force docDate={again.row.input?.doc_date ?? null}
          title={`ค้นใหม่ — ${again.row.model_code}`}
          onReviewPage onOpenReview={openRowById}
          // แถวขัดข้องไม่ถือดัชนี active — force อย่างเดียวพอ ไม่ไปทับข้อความขัดข้องเดิมด้วยคำว่า "ค้นใหม่"
          beforeEnqueue={!['approved', 'rejected', 'error'].includes(again.row.status)
            ? (mock ? async () => { mockPatch(again.row.id, { status: 'rejected', error: 'ค้นใหม่', finished_at: new Date().toISOString() }); }
              : () => db.rejectMaterial(again.row.id, 'ค้นใหม่'))
            : null}
          onDone={() => {
            if (mock) setRows(p => [mockBase({ id: `r-new-${Date.now()}`, status: 'queued', brand: again.row.brand, model_code: again.row.model_code, skus: skuUnion(again.row), requested_at: new Date().toISOString() }), ...p]);
            clearRowState(again.row.id);
          }}
          onClose={() => { setAgain(null); load({ quiet: true }); }}/>
      )}
    </div>
  );
}
